import { useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash, Warning } from "@phosphor-icons/react";

/**
 * Fase 11 — Modal de confirmación de borrado de mensaje.
 *
 * Props:
 *   open, onOpenChange     — control estándar shadcn/ui.
 *   message                — objeto mensaje completo (para preview).
 *   onConfirm()            — callback async que ejecuta el borrado.
 *
 * El texto del CTA cambia de "Borrar" → "Borrando…" mientras
 * onConfirm está en vuelo. Cualquier throw dentro de onConfirm se
 * atrapa aquí y se re-lanza sin cerrar el diálogo para que el
 * llamante tenga control del toast de error.
 */
export default function DeleteMessageDialog({ open, onOpenChange, message, onConfirm }) {
    const [busy, setBusy] = useState(false);

    if (!message) return null;
    const preview = (message.body || "").slice(0, 100).trim();

    const handleConfirm = async () => {
        setBusy(true);
        try {
            await onConfirm();
            onOpenChange?.(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="delete-message-dialog">
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 shrink-0 rounded-full bg-rose-100 p-1.5">
                            <Trash size={18} weight="bold" className="text-rose-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogTitle>Borrar mensaje</DialogTitle>
                            <DialogDescription>
                                Esta acción no se puede deshacer.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        data-testid="delete-message-preview"
                    >
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
                            {message.sender_name || "Mensaje"}
                        </div>
                        <p className="whitespace-pre-wrap break-words line-clamp-3">
                            {preview || "(sin contenido)"}
                            {(message.body || "").length > 100 ? "…" : ""}
                        </p>
                    </div>

                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
                        <span>
                            Este mensaje se borrará también en KimKim si es posible.
                            Si no se puede (mensaje antiguo, permisos, sesión caducada)
                            desaparecerá solo del Hub.
                        </span>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange?.(false)}
                        disabled={busy}
                        data-testid="delete-message-cancel-btn"
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={busy}
                        data-testid="delete-message-confirm-btn"
                    >
                        {busy ? "Borrando…" : "Borrar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
